// Where the reporter was when they filed. Nexus stores these three fields on
// the submission and the handoff prompt puts them in front of the agent, so a
// wrong label sends someone to the wrong screen.
//
// Matched longest-prefix-first: '/quotes/new' must win over '/quotes'.

export type PageContext = {
  module: string;
  page_name: string;
  page_url: string;
};

const ROUTES: Array<{ prefix: string; module: string; page_name: string }> = [
  { prefix: '/quotes/new', module: 'New order', page_name: 'QuoteNew' },
  { prefix: '/quotes', module: 'Orders', page_name: 'QuoteList' },
  { prefix: '/addresses', module: 'Address book', page_name: 'AddressBook' },
  { prefix: '/technology-request', module: 'Technology request', page_name: 'TechnologyRequest' },
  { prefix: '/employee/jobs', module: 'Employee print jobs', page_name: 'EmployeeJobs' },
  { prefix: '/employee/customers', module: 'Employee customers', page_name: 'EmployeeCustomers' },
  { prefix: '/checkout', module: 'Checkout', page_name: 'Checkout' },
];

// A quote route carries an id, and the checkout step hangs off it — read the
// deepest segment so an order being paid for is not reported as the list.
function resolveQuoteRoute(pathname: string): { module: string; page_name: string } | null {
  if (!pathname.startsWith('/quotes/')) return null;
  if (pathname.endsWith('/checkout')) return { module: 'Checkout', page_name: 'Checkout' };
  if (pathname === '/quotes/new') return null;
  return { module: 'Order', page_name: 'QuoteEdit' };
}

export function resolvePageContext(pathname: string, href: string): PageContext {
  const quote = resolveQuoteRoute(pathname);
  if (quote) return { ...quote, page_url: href };

  const match = ROUTES.filter((r) => pathname === r.prefix || pathname.startsWith(`${r.prefix}/`))
    .sort((a, b) => b.prefix.length - a.prefix.length)[0];

  if (match) return { module: match.module, page_name: match.page_name, page_url: href };
  if (pathname === '/') return { module: 'Home', page_name: 'Home', page_url: href };

  // An unmapped route still reports something usable rather than an empty
  // field — the URL is always right even when the label is unknown.
  const seg = pathname.replace(/^\//, '').split('/')[0] ?? '';
  return {
    module: seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : 'Portal',
    page_name: seg || 'Unknown',
    page_url: href,
  };
}
